<template>
  <div class="time-filter-panel">
    <!-- Date Range Filter -->
    <div class="filter-section mb-4">
      <div class="filter-label mb-2">
        <font-awesome-icon
          :icon="['fas', 'calendar-alt']"
          class="mr-2 primary--text"
          size="sm"
        />
        <span class="font-weight-bold">Date Range</span>
      </div>
      <date-range-picker-button
        :initial-start-date="pendingFilters.startDate"
        :initial-end-date="pendingFilters.endDate"
        @apply-date-range="onApplyDateRange"
        @clear-date-range="onClearDateRange"
      />
    </div>

    <!-- Person Filter -->
    <div
      v-if="personFilterItems && personFilterItems.length > 0"
      class="filter-section mb-4"
    >
      <div class="filter-label mb-2">
        <font-awesome-icon
          :icon="['fas', 'user']"
          class="mr-2 primary--text"
          size="sm"
        />
        <span class="font-weight-bold">Person</span>
      </div>
      <multi-select-autocomplete
        :value="pendingFilters.selectedPersonFilter"
        :items="personFilterItems"
        label="All Persons"
        :icon="['fas', 'user']"
        @input="updateFilter('selectedPersonFilter', $event)"
      />
    </div>

    <!-- Client Filter -->
    <div
      v-if="clientFilterItems && clientFilterItems.length > 0"
      class="filter-section mb-4"
    >
      <div class="filter-label mb-2">
        <font-awesome-icon
          :icon="['fas', 'building']"
          class="mr-2 primary--text"
          size="sm"
        />
        <span class="font-weight-bold">Client</span>
      </div>
      <multi-select-autocomplete
        :value="pendingFilters.selectedClientFilter"
        :items="clientFilterItems"
        label="All Clients"
        :icon="['fas', 'building']"
        @input="updateFilter('selectedClientFilter', $event)"
      />
    </div>

    <!-- Time Type Filter -->
    <div
      v-if="timeTypeFilterItems && timeTypeFilterItems.length > 0"
      class="filter-section mb-4"
    >
      <div class="filter-label mb-2">
        <font-awesome-icon
          :icon="['fas', 'clock']"
          class="mr-2 primary--text"
          size="sm"
        />
        <span class="font-weight-bold">Time Type</span>
      </div>
      <multi-select-autocomplete
        :value="pendingFilters.selectedTimeTypeFilter"
        :items="timeTypeFilterItems"
        label="All Time Types"
        :icon="['fas', 'clock']"
        @input="updateFilter('selectedTimeTypeFilter', $event)"
      />
    </div>

    <!-- Project Filter -->
    <div
      v-if="projectFilterItems && projectFilterItems.length > 0"
      class="filter-section mb-4"
    >
      <div class="filter-label mb-2">
        <font-awesome-icon
          :icon="['fas', 'folder']"
          class="mr-2 primary--text"
          size="sm"
        />
        <span class="font-weight-bold">Project</span>
      </div>
      <multi-select-autocomplete
        :value="pendingFilters.selectedProjectFilter"
        :items="projectFilterItems"
        label="All Projects"
        :icon="['fas', 'folder']"
        @input="updateFilter('selectedProjectFilter', $event)"
      />
    </div>
  </div>
</template>

<script>
/**
 * TimeFilterPanel Component
 *
 * A filter panel for Time view that renders filter controls for date range,
 * person, client, time type, and project. Used inside ToolsDrawer's filter-controls slot.
 *
 * Emits 'preview' event on any filter change for live preview.
 */
import DateRangePickerButton from '@/tools/components/DateRangePickerButton.vue'
import MultiSelectAutocomplete from '@/tools/components/filter/MultiSelectAutocomplete.vue'

export default {
  name: 'TimeFilterPanel',

  components: {
    DateRangePickerButton,
    MultiSelectAutocomplete,
  },

  props: {
    appliedFilters: {
      type: Object,
      required: true,
    },
    personFilterItems: {
      type: Array,
      default: () => [],
    },
    clientFilterItems: {
      type: Array,
      default: () => [],
    },
    timeTypeFilterItems: {
      type: Array,
      default: () => [],
    },
    projectFilterItems: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      pendingFilters: {
        startDate: null,
        endDate: null,
        selectedPersonFilter: [],
        selectedClientFilter: [],
        selectedTimeTypeFilter: [],
        selectedProjectFilter: [],
      },
    }
  },

  watch: {
    // Initialize pending filters when applied filters change
    appliedFilters: {
      immediate: true,
      deep: true,
      handler(newFilters) {
        if (newFilters) {
          this.pendingFilters = { ...newFilters }
        }
      },
    },
  },

  methods: {
    onApplyDateRange({ startDate, endDate }) {
      this.pendingFilters.startDate = startDate
      this.pendingFilters.endDate = endDate
      this.emitPreview()
    },

    onClearDateRange() {
      this.pendingFilters.startDate = null
      this.pendingFilters.endDate = null
      this.emitPreview()
    },

    updateFilter(filterKey, value) {
      this.pendingFilters[filterKey] = value
      this.emitPreview()
    },

    emitPreview() {
      this.$emit('preview', { ...this.pendingFilters })
    },

    // Called by parent (ToolsDrawer) when Apply is clicked
    getFilters() {
      return { ...this.pendingFilters }
    },

    // Called by parent (ToolsDrawer) when Reset is clicked
    resetFilters() {
      this.pendingFilters = {
        startDate: null,
        endDate: null,
        selectedPersonFilter: [],
        selectedClientFilter: [],
        selectedTimeTypeFilter: [],
        selectedProjectFilter: [],
      }
      this.emitPreview()
    },
  },
}
</script>

<style scoped lang="scss">
.time-filter-panel {
  .filter-section {
    .filter-label {
      display: flex;
      align-items: center;
      font-size: 0.875rem;
    }
  }
}
</style>
